//TODO remove eslint disable comment
/* eslint-disable @typescript-eslint/consistent-type-assertions */
import { Icon } from '@toggle/design-system';
import { mailTo } from '@toggle/helpers';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { useThemeStore } from '~/components/theme-controller/stores/useThemeStore';
import { config } from '~/config/app.config';
import { useQueryParams } from '~/hooks/use-query-params/useQueryParams';
import { useWatchlistActions } from '~/hooks/use-watchlist-actions/useWatchlistActions';
import { appPaths } from '~/routes/app-paths';
import { queryKeys } from '~/routes/query-params';
import { usePosthogEvents } from '~/services/tracking/posthog/use-posthog-events/usePosthogEvents';
import { useAuth } from '~/stores/use-auth/useAuth';
import { useSubscription } from '~/stores/use-subscription/useSubscription';
import { useChat } from '~/views/alfred/research/use-chat/useChat';
import { useChatNavigation } from '~/views/alfred/use-chat-navigation/useChatNavigation';
import { widgetPaths } from '~/views/widgets/widget-utils';

import * as S from './AppHeader.styles';
import {
  HeaderCategory,
  HeaderCategoryItem,
} from './header-navigation/components/category-group/HeaderCategoryGroup';
import {
  getActiveNavigationKey,
  isNewTabClick,
  mapNavigationKeyToPathname,
  navigationKeys,
} from './utils/navigation-utils';

export const FEEDBACK_SUPPORT_EMAIL = 'support@reflexivity.com';

// eslint-disable-next-line max-lines-per-function
export const AppHeader = () => {
  const { t } = useTranslation(['screener', 'common']);
  const navigate = useNavigate();
  const { setQueryParams } = useQueryParams();
  const {
    trackDashboardOpen,
    trackChartOpen,
    trackSearchOpen,
    trackWatchlistOpen,
    trackNewsOpen,
    trackInsightsOpen,
    trackScenarioOpen,
    trackScreenerOpen,
    trackStreamOpen,
  } = usePosthogEvents();

  const { logout } = useAuth();
  const colorScheme = useThemeStore(state => state.colorScheme);
  const isBasicUser = useSubscription(state => state.isBasicUser());
  const isBasicOrLightUser = useSubscription(state =>
    state.isBasicOrLightUser()
  );
  const { navigateToWatchlist } = useWatchlistActions();
  const { navigateToConversation } = useChatNavigation();

  const {
    unreadConversations,
    completeUnreadConversationCount,
    errorUnreadConversationCount,
  } = useChat(state => ({
    unreadConversations: state.getUnreadConversations(),
    completeUnreadConversationCount:
      state.getCompleteUnreadConversations().length,
    errorUnreadConversationCount: state.getErrorUnreadConversations().length,
  }));

  const badgeLabel =
    completeUnreadConversationCount > 9
      ? '+9'
      : completeUnreadConversationCount;

  const isProcessing = unreadConversations.some(c => c.status === 'Processing');

  const defaultNavigationHandler = (categoryItem: HeaderCategoryItem) => {
    navigate(mapNavigationKeyToPathname[categoryItem.key]);
  };

  const newTabNavigationHandler = (categoryItem: HeaderCategoryItem) =>
    window.open(
      mapNavigationKeyToPathname[categoryItem.key],
      '_blank',
      'noopener,noreferrer'
    );

  const showNotifications =
    !!completeUnreadConversationCount || !!errorUnreadConversationCount;

  const alfredItem: HeaderCategoryItem = {
    label: t('common:sideNavigation.alfred'),
    pillStyle: true,
    isAlfred: true,
    key: navigationKeys.alfred,
    icon: <Icon iconName="Alfred" size={20} />,
    onClick: (item, e) => {
      isNewTabClick(e)
        ? newTabNavigationHandler(item)
        : defaultNavigationHandler(item);
    },
    isDashboardUpdatesEnabled: true,
    isProcessing: isProcessing,
    isFinished: !!completeUnreadConversationCount,
    notification: showNotifications && (
      <>
        {!!errorUnreadConversationCount && (
          <S.StyledErrorBadge
            variant="numerical"
            size="small"
            label="!"
            $withCounterBadge={!!completeUnreadConversationCount}
          />
        )}
        {!!completeUnreadConversationCount && (
          <S.StyledCounterBadge
            variant="numerical"
            size="small"
            label={badgeLabel}
            aria-label={t('common:notifications.count', {
              count: completeUnreadConversationCount,
            })}
            $withError={!!errorUnreadConversationCount}
          />
        )}
      </>
    ),
    unreadConversations: unreadConversations,
    onConversationClick: (conversationId: string) => {
      navigateToConversation(conversationId);
    },
  };

  const basicLightCategories: HeaderCategory[] = [
    {
      id: 'menu',
      items: [
        {
          label: t('common:sideNavigation.dashboard'),
          key: navigationKeys.dashboard,
          onClick: (item, e) => {
            trackDashboardOpen('nav');
            isNewTabClick(e)
              ? newTabNavigationHandler(item)
              : defaultNavigationHandler(item);
          },
        },
        {
          label: t('common:sideNavigation.watchlist'),
          key: navigationKeys.watchlist,
          onClick: (_, e) => {
            trackWatchlistOpen('nav');
            navigateToWatchlist({ openInNewTab: isNewTabClick(e) });
          },
        },
        {
          label: t('common:sideNavigation.screener'),
          key: navigationKeys.screener,
          onClick: (_, e) => {
            trackScreenerOpen('nav');

            if (isNewTabClick(e)) {
              window.open(appPaths.screener, '_blank', 'noopener,noreferrer');
            } else {
              navigate(appPaths.screener);
            }
          },
        },
        {
          label: t('common:sideNavigation.explore'),
          key: navigationKeys.insights,
          onClick: (item, e) => {
            trackInsightsOpen('nav');
            isNewTabClick(e)
              ? newTabNavigationHandler(item)
              : defaultNavigationHandler(item);
          },
        },
        {
          label: t('common:sideNavigation.news'),
          key: navigationKeys.news,
          onClick: (item, e) => {
            trackNewsOpen('nav');
            isNewTabClick(e)
              ? newTabNavigationHandler(item)
              : defaultNavigationHandler(item);
          },
        },
        {
          label: t('common:sideNavigation.chart'),
          key: navigationKeys.chart,
          onClick: (item, e) => {
            trackChartOpen('nav');
            isNewTabClick(e)
              ? newTabNavigationHandler(item)
              : defaultNavigationHandler(item);
          },
        },
        ...(isBasicUser
          ? []
          : ([
              {
                label: t('common:sideNavigation.events'),
                key: navigationKeys.events,
                onClick: (item, e) => {
                  isNewTabClick(e)
                    ? newTabNavigationHandler(item)
                    : defaultNavigationHandler(item);
                },
              },
              {
                label: t('common:sideNavigation.scenario'),
                key: navigationKeys.scenario,
                onClick: (item, e) => {
                  trackScenarioOpen('nav');
                  isNewTabClick(e)
                    ? newTabNavigationHandler(item)
                    : defaultNavigationHandler(item);
                },
              },
            ] as HeaderCategoryItem[])),
        alfredItem,
      ],
    },
  ];

  const categories: HeaderCategory[] = [
    {
      id: 'menu',
      items: [
        {
          label: t('common:sideNavigation.dashboard'),
          key: navigationKeys.dashboard,
          onClick: (item, e) => {
            trackDashboardOpen('nav');
            isNewTabClick(e)
              ? newTabNavigationHandler(item)
              : defaultNavigationHandler(item);
          },
        },
        {
          label: t('common:sideNavigation.stream'),
          key: navigationKeys.stream,
          onClick: (
            item: HeaderCategoryItem,
            e: React.MouseEvent<Element, MouseEvent> | undefined
          ) => {
            trackStreamOpen('nav');
            isNewTabClick(e)
              ? newTabNavigationHandler(item)
              : defaultNavigationHandler(item);
          },
        },
        {
          label: t('common:sideNavigation.watchlist'),
          key: navigationKeys.watchlist,
          onClick: (_, e) => {
            trackWatchlistOpen('nav');
            navigateToWatchlist({ openInNewTab: isNewTabClick(e) });
          },
        },
        {
          label: t('common:sideNavigation.chart'),
          key: navigationKeys.chart,
          onClick: (
            item: HeaderCategoryItem,
            e: React.MouseEvent<Element, MouseEvent> | undefined
          ) => {
            trackChartOpen('nav');
            isNewTabClick(e)
              ? newTabNavigationHandler(item)
              : defaultNavigationHandler(item);
          },
        },
        {
          label: t('common:sideNavigation.more'),
          key: navigationKeys.more,
          icon: (
            <S.MoreDropdownContainer>
              <S.MoreDropdownLabel>
                {t('common:sideNavigation.more')}
              </S.MoreDropdownLabel>
              <Icon iconName="ChevronLightDown" size={12} />
            </S.MoreDropdownContainer>
          ),
          onClick: () => {},
          dropdownItems: [
            [
              {
                label: t('common:sideNavigation.screener'),
                key: navigationKeys.screener,
                onClick: (e?: Event | React.MouseEvent) => {
                  trackScreenerOpen('nav');
                  const navItem = {
                    key: navigationKeys.screener,
                  } as HeaderCategoryItem;
                  isNewTabClick(e as React.MouseEvent | undefined)
                    ? newTabNavigationHandler(navItem)
                    : defaultNavigationHandler(navItem);
                },
              },
              {
                label: t('common:sideNavigation.explore'),
                key: navigationKeys.insights,
                onClick: (e?: Event | React.MouseEvent) => {
                  trackInsightsOpen('nav');
                  const navItem = {
                    key: navigationKeys.insights,
                  } as HeaderCategoryItem;
                  isNewTabClick(e as React.MouseEvent | undefined)
                    ? newTabNavigationHandler(navItem)
                    : defaultNavigationHandler(navItem);
                },
              },
              {
                label: t('common:sideNavigation.news'),
                key: navigationKeys.news,
                onClick: (e?: Event | React.MouseEvent) => {
                  trackNewsOpen('nav');
                  const navItem = {
                    key: navigationKeys.news,
                  } as HeaderCategoryItem;
                  isNewTabClick(e as React.MouseEvent | undefined)
                    ? newTabNavigationHandler(navItem)
                    : defaultNavigationHandler(navItem);
                },
              },

              {
                label: t('common:sideNavigation.events'),
                key: navigationKeys.events,
                onClick: (e?: Event | React.MouseEvent) => {
                  const navItem = {
                    key: navigationKeys.events,
                  } as HeaderCategoryItem;
                  isNewTabClick(e as React.MouseEvent | undefined)
                    ? newTabNavigationHandler(navItem)
                    : defaultNavigationHandler(navItem);
                },
              },
              {
                label: t('common:sideNavigation.scenario'),
                key: navigationKeys.scenario,
                onClick: (e?: Event | React.MouseEvent) => {
                  trackScenarioOpen('nav');
                  const navItem = {
                    key: navigationKeys.scenario,
                  } as HeaderCategoryItem;
                  isNewTabClick(e as React.MouseEvent | undefined)
                    ? newTabNavigationHandler(navItem)
                    : defaultNavigationHandler(navItem);
                },
              },
            ],
          ],
        },
        alfredItem,
      ],
    },
  ];

  const activeCategories = isBasicOrLightUser
    ? basicLightCategories
    : categories;

  const categoriesOptions: HeaderCategory[] = [
    {
      id: 'options',
      items: [
        {
          key: navigationKeys.help,
          label: t('common:sideNavigation.help'),
          icon: <Icon iconName="QuestionMarkCircle" size={24} />,
          showTooltip: true,
          onClick: () => {
            window.zE('webWidget', 'open');
            window.zE('webWidget', 'show');
          },
        },
        {
          key: navigationKeys.settings,
          label: t('common:sideNavigation.settings'),
          icon: <Icon iconName="Settings" size={24} />,
          showTooltip: true,
          onClick: () => {},
          dropdownItems: [
            [
              {
                key: navigationKeys.settings,
                label: t('common:sideNavigation.account'),
                onClick: () =>
                  navigate(mapNavigationKeyToPathname[navigationKeys.settings]),
              },
              {
                key: navigationKeys.subscription,
                label: t('common:sideNavigation.subscription'),
                onClick: () =>
                  navigate(
                    mapNavigationKeyToPathname[navigationKeys.subscription]
                  ),
              },
              {
                key: navigationKeys.portfolio,
                label: t('common:sideNavigation.portfolio'),
                onClick: () =>
                  navigate(
                    mapNavigationKeyToPathname[navigationKeys.portfolio]
                  ),
              },
              {
                key: navigationKeys.appearance,
                label: t('common:sideNavigation.appearance'),
                onClick: () =>
                  navigate(
                    mapNavigationKeyToPathname[navigationKeys.appearance]
                  ),
              },
            ],
            [
              {
                label: t('common:sideNavigation.onboarding'),
                key: navigationKeys.onboarding,
                iconName: 'Headset',
                onClick: () => {
                  window.open(
                    config.urls.onboardingUrl,
                    '_blank',
                    'noopener,noreferrer'
                  );
                },
              },
              {
                label: t('common:sideNavigation.feedback'),
                key: navigationKeys.feedback,
                iconName: 'Feedback',
                onClick: () => {
                  const subject = t('common:sideNavigation.terminalFeedback');
                  window.location.href = mailTo(FEEDBACK_SUPPORT_EMAIL, {
                    subject,
                  });
                },
              },
            ],
            [
              {
                label: t('common:sideNavigation.signOut'),
                key: navigationKeys.signOut,
                iconName: 'SignOut',
                onClick: () => logout(),
              },
            ],
          ],
        },
      ],
    },
  ];

  return (
    <S.StyledHeaderNavigation
      activeKey={getActiveNavigationKey([
        ...activeCategories,
        ...categoriesOptions,
      ])}
      colorScheme={colorScheme}
      onLogoClick={() => {
        navigate(appPaths.base);
      }}
      onSearchClick={() => {
        trackSearchOpen('nav');
        setQueryParams({
          [queryKeys.widget]: widgetPaths.searchAsset,
        });
      }}
      categories={activeCategories}
      categoriesOptions={categoriesOptions}
    />
  );
};
